/**
 * UK Postcode Validation Utilities
 * 
 * Provides validation and formatting for UK postcodes
 */

/**
 * Validates UK postcode format
 * 
 * Supports formats like:
 * - SW1A 1AA
 * - M1 1AA
 * - CR2 6XH
 * - DN55 1PT
 * - W1A 0AX
 * - EC1A 1BB
 * 
 * @param postcode - The postcode to validate
 * @returns true if valid UK postcode format
 */
export function isValidUKPostcode(postcode: string): boolean {
  // UK postcode regex pattern
  // Matches: A9 9AA, A99 9AA, AA9 9AA, AA99 9AA, A9A 9AA, AA9A 9AA
  const ukPostcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i
  return ukPostcodeRegex.test(postcode.trim())
}

/**
 * Formats a UK postcode to standard format with space
 * 
 * Examples:
 * - "SW1A1AA" -> "SW1A 1AA"
 * - "m11aa" -> "M1 1AA"
 * - "SW1A 1AA" -> "SW1A 1AA" (already formatted)
 * 
 * @param postcode - The postcode to format
 * @returns Formatted postcode with uppercase and space
 */
export function formatPostcode(postcode: string): string {
  // Remove all whitespace and convert to uppercase
  const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, '')
  
  // Add space in correct position (before last 3 characters)
  if (cleaned.length >= 5) {
    const outcode = cleaned.slice(0, -3)
    const incode = cleaned.slice(-3)
    return `${outcode} ${incode}`
  }
  
  return cleaned
}

/**
 * Validates and formats a postcode in one step
 * 
 * @param postcode - The postcode to validate and format
 * @returns Object with validation result and formatted postcode
 */
export function validateAndFormatPostcode(postcode: string): {
  isValid: boolean
  formatted: string
  error?: string
} {
  if (!postcode || !postcode.trim()) {
    return {
      isValid: false,
      formatted: '',
      error: 'Postcode is required',
    }
  }

  const formatted = formatPostcode(postcode)
  const isValid = isValidUKPostcode(formatted)

  return {
    isValid,
    formatted,
    error: isValid ? undefined : 'Please enter a valid UK postcode (e.g., SW1A 1AA)',
  }
}

